import { useContext, createContext, useMemo, useState, useEffect, useRef } from "react";
import { UseGoogleSheet } from "../hooks/UseGoogleSheets";

const DEFAULT_CACHE_TTL_MS = 60 * 1000; // 1 minute

const AppContext = createContext({
	products: null,
	orderLabels: null,
	setOrderLabels: () => { },
	setProducts: () => { },
	currentTime: null,
	setCurrentTime: () => { },
	loggedTime: null,
	setLoggedTime: () => { },
	timeRemaining: 59,
	setTimeRemaining: () => { },
	rows: 0,
	setRows: () => { },
	currentView: null,
	setCurrentView: () => { },
	globalCount: 0,
	setGlobalCount: () => { },
	fsUnits: null,
	setFsUnits: () => { },
	setQCFields: () => { },
	isExternal: true,
	setIsExternal: () => { },

	// Sheet helpers
	loadRawSheet: async () => [],
	loadSheetAsJson: async () => [],
	loadSheetsBatchAsJson: async () => [],
	addRowToSheet: async () => { },
	updateRowInSheet: async () => { },
	createObj: async () => [],
	saveSuccess: () => { },
	clearSheetJsonCache: () => { },
	getObjectByKeyValue: () => { },
	getObjectByUUID: () => { },
	getObjectByUUIDAlt: async () => { },

	// Spreadsheet switching
	spreadsheetId: undefined,
	setSpreadsheetId: () => { },
	switchSpreadsheet: async () => { },
	loadSpreadSheetByID: async () => { },
	getCurrentSpreadsheetID: () => {},


	// Raw hook bits
	loadSheet: async () => [],
	sheetRows: [],
	setRow: () => { },
	addRow: () => { },
	saveRow: () => { },
});

export const useAppContext = () => useContext(AppContext);

export const getLoggedtime = () => {
	const loggedTime = localStorage.getItem("loggedTime");
	return loggedTime || null;
};

export const AppProvider = ({ children }) => {
	const [products, setProducts] = useState(null);
	const [currentTime, setCurrentTime] = useState(null);
	const [timeRemaining, setTimeRemaining] = useState(59);
	const [loggedTime, setLoggedTime] = useState(getLoggedtime());
	const [rows, setRows] = useState(59);
	const [orderLabels, setOrderLabels] = useState(null);
	const [currentView, setCurrentView] = useState("rvmp");
	const [globalCount, setGlobalCount] = useState(0);
	const [fsUnits, setFsUnits] = useState(0);
	const [qcFields, setQCFields] = useState(0);
	const [isExternal, setIsExternal] = useState(false);

	const [spreadsheetId, setSpreadsheetId] = useState(process.env.REACT_APP_FS_Flock_V1);

	const switchResolveRef = useRef(null);

	const { loadSheet, sheetRows, setRow, addRow, saveRow: _saveRow } = UseGoogleSheet({
		spreadsheetId,
	});

	const cacheKeyFor = (id) => `sheetJson:${id}`;
	const readCache = (id = spreadsheetId, ttlMs = DEFAULT_CACHE_TTL_MS) => {
		try {
			const raw = localStorage.getItem(cacheKeyFor(id));
			if (!raw) return null;
			const { data, ts } = JSON.parse(raw);
			if (typeof ts !== "number" || Date.now() - ts > ttlMs) return null;
			return Array.isArray(data) ? data : null;
		} catch {
			return null;
		}
	};
	const writeCache = (data, id = spreadsheetId) => {
		try {
			localStorage.setItem(cacheKeyFor(id), JSON.stringify({ ts: Date.now(), data }));
		} catch { }
	};
	const clearSheetJsonCache = (id = spreadsheetId) => {
		try {
			localStorage.removeItem(cacheKeyFor(id));
		} catch { }
	};

	useEffect(() => {
		if (!spreadsheetId) return;
		clearSheetJsonCache(spreadsheetId);
		if (switchResolveRef.current) {
			switchResolveRef.current();
			switchResolveRef.current = null;
		}
	}, [spreadsheetId]);

	const rowToSparseObject = (row, { trim = true } = {}) => {
		const headers = row?._sheet?.headerValues || row?._worksheet?.headerValues || [];
		const isNonEmpty = (v) => {
			if (v === null || v === undefined) return false;
			if (typeof v === "string") return trim ? v.trim() !== "" : v !== "";
			return true;
		};
		const getCell = (idx, header) => {
			if (Array.isArray(row?._rawData)) return row._rawData[idx];
			if (typeof row?.get === "function") {
				try {
					return row.get(header);
				} catch { }
			}
			return undefined;
		};
		const out = {};
		headers.forEach((h, i) => {
			const val = getCell(i, h);
			const norm = typeof val === "string" && trim ? val.trim() : val;
			if (isNonEmpty(norm)) out[h] = norm;
		});
		return out;
	};

	const loadRawSheet = (force = true) => loadSheet(force);

	const loadSheetAsJson = async (force = false, opts = { trim: true }) => {
		if (!force) {
			const cached = readCache();
			if (cached) return cached;
		}
		const rows = await loadSheet(true);
		const json = Array.isArray(rows) ? rows.map((r) => rowToSparseObject(r, opts)) : [];
		writeCache(json);
		return json;
	};

	const latestRef = useRef({
		loadSheetAsJson: loadSheetAsJson,
	});
	useEffect(() => {
		latestRef.current.loadSheetAsJson = loadSheetAsJson;
	}, [loadSheetAsJson, spreadsheetId]);

	const switchSpreadsheet = async (nextId, options = {}) => {
		const { preload = true, force = true, clearCache = true } = options;
		if (!nextId) return;
		if (nextId === spreadsheetId) {
			if (preload) await latestRef.current.loadSheetAsJson(force);
			return;
		}
		if (clearCache) clearSheetJsonCache(nextId);
		await new Promise((resolve) => {
			switchResolveRef.current = resolve;
			setSpreadsheetId(nextId);
		});
		if (preload && latestRef.current.loadSheetAsJson) {
			await latestRef.current.loadSheetAsJson(force);
		}
	};

	const loadSpreadSheetByID = async (nextId, options = {}) => {
		return switchSpreadsheet(nextId, { preload: true, force: true, clearCache: true, ...options });
	};

	const getCurrentSpreadsheetID = () => spreadsheetId;  // ✅ NEW FUNCTION

	const loadSheetsBatchAsJson = async (ids = [], options = {}) => {
		const { force = false, trim = true, restore = true } = options;
		if (!Array.isArray(ids) || ids.length === 0) return [];
		const originalId = spreadsheetId;
		const results = [];

		for (const id of ids) {
			if (!force) {
				const cached = readCache(id);
				if (cached) {
					results.push({ spreadsheetId: id, data: cached, cached: true });
					continue;
				}
			}
			await new Promise((resolve) => {
				switchResolveRef.current = resolve;
				setSpreadsheetId(id);
			});
			const data = await latestRef.current.loadSheetAsJson(true, { trim });
			results.push({ spreadsheetId: id, data, cached: false });
		}

		if (restore && originalId && originalId !== spreadsheetId) {
			await new Promise((resolve) => {
				switchResolveRef.current = resolve;
				setSpreadsheetId(originalId);
			});
			if (latestRef.current.loadSheetAsJson) {
				await latestRef.current.loadSheetAsJson(false, { trim });
			}
		}

		return results;
	};

	const addRowToSheet = async (obj, message = "Added row to sheet") => {
		await addRow(obj);
		clearSheetJsonCache();
		return message;
	};

	const updateRowInSheet = async (obj, message = "Updated row in sheet") => {
		await loadSheet(true);
		const row = sheetRows?.find((r) => r.get("UUID") === obj?.UUID);
		if (!row) {
			alert("Could not locate the row");
			return "Could not locate the row";
		}
		await Promise.all(Object.keys(obj).map((key) => setRow(row, key, obj[key])));
		await saveRow(row);
		return message;
	};

	const getObjectByKeyValue = (arr, key, value) =>
		Array.isArray(arr) && arr.length > 0 ? arr.find((obj) => obj[key] === value) || null : null;

	const getObjectByUUID = (arr, uuid, selector = "UUID") => getObjectByKeyValue(arr, selector, uuid);

	const getObjectByUUIDAlt = async (data, uuid) => {
		return data.find((item) => item.UUID === uuid);
	};

	const createObj = (rowsOfUnits1) => {
		const temp = [];
		rowsOfUnits1.forEach((row) => {
			temp.push({
				UUID: row.get("UUID"),
			});
		});
		return Promise.resolve(temp);
	};

	const saveSuccess = (target = null, url = null) => {
		if (!target) return;
		setTimeout(() => {
			target.classList.remove("_loading");
			target.classList.add("_success");
			setTimeout(() => {
				target.classList.remove("_success");
				if (url) {
					window.scrollTo(0, 0);
					window.location.href = url;
				}
			}, 1000);
		}, 750);
	};

	const saveRow = async (...args) => {
		const res = await _saveRow(...args);
		clearSheetJsonCache();
		return res;
	};

	const value = useMemo(
		() => ({
			products, setProducts,
			currentTime, setCurrentTime,
			loggedTime, setLoggedTime,
			timeRemaining, setTimeRemaining,
			rows, setRows,
			orderLabels, setOrderLabels,
			currentView, setCurrentView,
			globalCount, setGlobalCount,
			fsUnits, setFsUnits,
			qcFields, setQCFields,
			isExternal, setIsExternal,

			spreadsheetId,
			setSpreadsheetId,
			switchSpreadsheet,
			loadSpreadSheetByID,
			getCurrentSpreadsheetID,

			loadRawSheet,
			loadSheetAsJson,
			loadSheetsBatchAsJson,
			addRowToSheet,
			updateRowInSheet,
			createObj,
			saveSuccess,
			clearSheetJsonCache,
			getObjectByKeyValue,
			getObjectByUUID,
			getObjectByUUIDAlt,

			loadSheet,
			sheetRows,
			setRow,
			addRow,
			saveRow,
		}),
		[
			products, currentTime, loggedTime, timeRemaining, rows, orderLabels,
			currentView, globalCount, fsUnits, qcFields, sheetRows, spreadsheetId, isExternal
		]
	);

	return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
